import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Building2,
  User,
  Phone,
  MapPin,
  Home,
  Landmark,
  DollarSign,
  Send,
  CheckCircle,
  Loader2,
  Maximize,
  Bed,
  Bath,
  Calendar,
  Sparkles,
  Compass,
  Layers,
  FileText,
  Zap,
  Droplet,
  Image as ImageIcon,
  Video as VideoIcon,
  X
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useTranslation } from 'react-i18next';
import offersService from '../services/offersService';
import locationService from '../services/locationService';
import './PublicOfferForm.css';

const DIRECTIONS = ['north', 'south', 'east', 'west', 'northeast', 'northwest', 'southeast', 'southwest'];
const DIRECTION_LABELS = {
  north: 'شمالي',
  south: 'جنوبي',
  east: 'شرقي',
  west: 'غربي',
  northeast: 'شمالي شرقي',
  northwest: 'شمالي غربي',
  southeast: 'جنوبي شرقي',
  southwest: 'جنوبي غربي'
};

const initialFormData = {
  title: '',
  ownerName: '',
  ownerPhone: '',
  propertyType: 'villa',
  offerType: 'sale',
  city: '',
  region: '',
  district: '',
  price: '',
  priceOnRequest: false,
  area: '',
  rooms: '',
  bathrooms: '',
  age: '',
  direction: '',
  streetWidth: '',
  floors: '',
  deedType: '',
  acceptsPayments: 'no',
  offerNature: 'direct',
  electricity: 'independent',
  water: 'independent',
  comment: '',
  description: '',
  features: []
};

const PublicOfferForm = () => {
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'ar';
  const [searchParams] = useSearchParams();
  const referralUserId = searchParams.get('ref');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [cities, setCities] = useState([]);
  const [areasByCity, setAreasByCity] = useState({});
  const [districtsByArea, setDistrictsByArea] = useState({});
  const [allDistrictsByCity, setAllDistrictsByCity] = useState({});
  const [aiText, setAiText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [useAI, setUseAI] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [featureInput, setFeatureInput] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState([]);

  const propertyTypes = [
    { value: 'villa', labelKey: 'offers.villa', icon: Home },
    { value: 'apartment', labelKey: 'offers.apartment', icon: Building2 },
    { value: 'land', labelKey: 'offers.land', icon: Landmark },
    { value: 'duplex', labelKey: 'offers.duplex', icon: Building2 },
    { value: 'commercial', labelKey: 'offers.commercial', icon: Building2 },
    { value: 'building', labelKey: 'offers.building', icon: Building2 }
  ];

  // Fetch cities from API
  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoadingLocations(true);
        const response = await locationService.getAllCities(currentLang);
        if (response.success && response.data.cities) {
          const citiesData = response.data.cities;
          setCities(citiesData);
          const areasMap = {};
          const districtsMap = {};
          const allDistrictsMap = {};
          citiesData.forEach(city => {
            areasMap[city.label] = (city.areas || []).map(a => a.label);
            const cityAllDistricts = [];
            (city.areas || []).forEach(area => {
              const areaDistricts = (area.districts || []).map(d => d.label);
              districtsMap[area.label] = areaDistricts;
              cityAllDistricts.push(...areaDistricts);
            });
            (city.unassignedDistricts || []).forEach(d => cityAllDistricts.push(d.label));
            allDistrictsMap[city.label] = cityAllDistricts;
          });
          setAreasByCity(areasMap);
          setDistrictsByArea(districtsMap);
          setAllDistrictsByCity(allDistrictsMap);
        }
      } catch (err) {
        console.error('Error fetching cities:', err);
      } finally {
        setLoadingLocations(false);
      }
    };
    fetchCities();
  }, [currentLang]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    if (name === 'city') {
      setFormData(prev => ({ ...prev, [name]: newValue, region: '', district: '' }));
    } else if (name === 'region') {
      setFormData(prev => ({ ...prev, [name]: newValue, district: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: newValue }));
    }
  };

  const handleAddFeature = () => {
    const v = featureInput.trim();
    if (!v) return;
    if (formData.features.includes(v)) {
      setFeatureInput('');
      return;
    }
    setFormData(prev => ({ ...prev, features: [...prev.features, v] }));
    setFeatureInput('');
  };

  const handleRemoveFeature = (feature) => {
    setFormData(prev => ({ ...prev, features: prev.features.filter(f => f !== feature) }));
  };

  const handleImagesUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 10 - imageFiles.length;
    const allowed = files.slice(0, remaining);
    const newPreviews = allowed.map(f => URL.createObjectURL(f));
    setImageFiles(prev => [...prev, ...allowed]);
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  const handleRemoveImage = (index) => {
    URL.revokeObjectURL(imagePreviews[index]);
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleVideosUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 5 - videoFiles.length;
    const allowed = files.slice(0, remaining);
    const newPreviews = allowed.map(f => URL.createObjectURL(f));
    setVideoFiles(prev => [...prev, ...allowed]);
    setVideoPreviews(prev => [...prev, ...newPreviews]);
  };

  const handleRemoveVideo = (index) => {
    URL.revokeObjectURL(videoPreviews[index]);
    setVideoFiles(prev => prev.filter((_, i) => i !== index));
    setVideoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleAnalyzeText = async () => {
    if (!aiText.trim()) {
      toast.warning('الرجاء كتابة وصف العقار أولاً');
      return;
    }
    setAnalyzing(true);
    try {
      const response = await offersService.analyzePublicWithAI(aiText, formData.city);

      if (response.success) {
        const data = response.data.extractedData;

        // Match city
        let matchedCity = formData.city || data.city || '';
        if (matchedCity && !cities.some(c => c.label === matchedCity)) {
          const foundCity = cities.find(c =>
            c.label.includes(matchedCity) || matchedCity.includes(c.label)
          );
          if (foundCity) matchedCity = foundCity.label;
        }

        // Match district
        let matchedDistrict = data.district || '';
        if (matchedCity && matchedDistrict) {
          const availableDistricts = allDistrictsByCity[matchedCity] || [];
          if (!availableDistricts.includes(matchedDistrict)) {
            const found = availableDistricts.find(d =>
              d.includes(matchedDistrict) || matchedDistrict.includes(d)
            );
            if (found) matchedDistrict = found;
          }
        }

        setFormData(prev => ({
          ...prev,
          title: data.title || prev.title,
          ownerName: data.ownerName || prev.ownerName,
          ownerPhone: data.ownerPhone || prev.ownerPhone,
          propertyType: data.propertyType || prev.propertyType,
          offerType: data.offerType || prev.offerType,
          city: matchedCity || prev.city,
          district: matchedDistrict || prev.district,
          price: data.price?.toString() || prev.price,
          area: data.area?.toString() || prev.area,
          rooms: data.rooms?.toString() || prev.rooms,
          bathrooms: data.bathrooms?.toString() || prev.bathrooms,
          age: data.age?.toString() || prev.age,
          streetWidth: data.streetWidth?.toString() || prev.streetWidth,
          floors: data.floors?.toString() || prev.floors,
          deedType: data.ownershipStatus || data.deedType || prev.deedType,
          offerNature: data.offerNature || prev.offerNature,
          description: aiText,
          features: Array.isArray(data.features) && data.features.length ? data.features : prev.features
        }));

        setUseAI(false);

        if (matchedCity && matchedDistrict) {
          toast.success('تم التحليل بنجاح! تم تعبئة جميع البيانات');
        } else if (matchedCity) {
          toast.success('تم التحليل! يرجى اختيار الحي يدوياً');
        } else {
          toast.success('تم التحليل! يرجى اختيار المدينة والحي يدوياً');
        }
      } else {
        toast.error('فشل التحليل: ' + (response.message || 'خطأ غير معروف'));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('فشل التحليل: ' + error.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!formData.ownerName || !formData.ownerPhone || !formData.city) {
      toast.warning('الرجاء إدخال اسم المالك ورقم الهاتف والمدينة');
      return;
    }
    if (!formData.priceOnRequest && !formData.price) {
      toast.warning('الرجاء إدخال السعر');
      return;
    }
    if (!formData.district) {
      toast.warning('الرجاء اختيار الحي');
      return;
    }
    if (formData.offerType === 'sale' && !formData.area && formData.propertyType !== 'land') {
      // matches marketer rule: area required for sale
    }

    setLoading(true);
    try {
      await offersService.submitPublicOffer(
        { ...formData, referralUserId },
        imageFiles,
        videoFiles,
        null // Public submissions also use the first image as their main image.
      );
      setSubmitted(true);
      toast.success('تم إرسال العرض بنجاح!');
    } catch (error) {
      console.error('Error submitting offer:', error);
      toast.error(error.message || 'حدث خطأ في إرسال العرض');
    } finally {
      setLoading(false);
    }
  }, [formData, toast, referralUserId, imageFiles, videoFiles]);

  const resetAll = () => {
    setFormData(initialFormData);
    setUseAI(false);
    setAiText('');
    setFeatureInput('');
    imagePreviews.forEach(URL.revokeObjectURL);
    videoPreviews.forEach(URL.revokeObjectURL);
    setImageFiles([]);
    setImagePreviews([]);
    setVideoFiles([]);
    setVideoPreviews([]);
  };

  if (submitted) {
    return (
      <div className="public-offer-page">
        <div className="success-container">
          <div className="success-icon">
            <CheckCircle size={80} />
          </div>
          <h1>تم إرسال العرض بنجاح!</h1>
          <p>شكراً لك، سيتم مراجعة العرض والتواصل معك قريباً</p>
          <div className="success-details">
            <div className="detail-row">
              <span>اسم المالك:</span>
              <strong>{formData.ownerName}</strong>
            </div>
            <div className="detail-row">
              <span>رقم الهاتف:</span>
              <strong>{formData.ownerPhone}</strong>
            </div>
            <div className="detail-row">
              <span>نوع العقار:</span>
              <strong>{t(propertyTypes.find(p => p.value === formData.propertyType)?.labelKey)}</strong>
            </div>
            <div className="detail-row">
              <span>نوع العرض:</span>
              <strong>{formData.offerType === 'sale' ? 'بيع' : 'إيجار'}</strong>
            </div>
            <div className="detail-row">
              <span>الموقع:</span>
              <strong>{formData.city} - {formData.district}</strong>
            </div>
            <div className="detail-row">
              <span>السعر:</span>
              <strong>
                {formData.priceOnRequest
                  ? 'لاحقاً'
                  : `${Number(formData.price).toLocaleString('ar-SA')} ر.س`}
              </strong>
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => { setSubmitted(false); resetAll(); }}
          >
            إرسال عرض جديد
          </button>
        </div>
      </div>
    );
  }

  const showSpecs = formData.propertyType !== 'land';
  const isRent = formData.offerType === 'rent';
  const isSale = formData.offerType === 'sale';

  return (
    <div className="public-offer-page">
      <div className="form-container">
        <div className="form-header">
          <div className="logo">
            <Building2 size={40} />
          </div>
          <h1>نموذج إضافة عرض عقاري</h1>
          <p>أضف تفاصيل العقار الذي تريد عرضه للبيع أو الإيجار</p>
        </div>

        <form onSubmit={handleSubmit} className="offer-form">
          {/* AI Toggle */}
          <div className="ai-toggle-section">
            <button
              type="button"
              className={`ai-toggle-btn ${useAI ? 'active' : ''}`}
              onClick={() => setUseAI(!useAI)}
            >
              <Sparkles size={20} />
              {useAI ? 'استخدام النموذج اليدوي' : 'استخدام الذكاء الاصطناعي'}
            </button>
          </div>

          {/* AI Text Input */}
          {useAI && (
            <div className="form-section ai-section">
              <h3>
                <Sparkles size={20} />
                استخدام الذكاء الاصطناعي
              </h3>
              <p className="ai-description">
                الصق وصف العقار من أي مصدر وسيقوم الذكاء الاصطناعي بتحليله وتعبئة البيانات تلقائياً
              </p>
              <div className="form-group">
                <textarea
                  value={aiText}
                  onChange={(e) => setAiText(e.target.value)}
                  rows={6}
                  placeholder="الصق هنا وصف العقار... مثال: فيلا للبيع في الرياض حي النرجس، المساحة 400م، 5 غرف، 4 حمامات، السعر 2,500,000 ريال"
                  disabled={analyzing}
                />
              </div>
              <button
                type="button"
                className="analyze-btn"
                onClick={handleAnalyzeText}
                disabled={analyzing || !aiText.trim()}
              >
                {analyzing ? (
                  <>
                    <Loader2 size={20} className="spinning" />
                    جاري التحليل...
                  </>
                ) : (
                  <>
                    <Sparkles size={20} />
                    تحليل النص
                  </>
                )}
              </button>
            </div>
          )}

          {!useAI && (
          <>
          {/* Owner Info */}
          <div className="form-section">
            <h3><User size={20} /> معلومات المالك</h3>
            <div className="form-row two-cols">
              <div className="form-group">
                <label>اسم المالك <span className="required">*</span></label>
                <div className="input-with-icon">
                  <User size={18} />
                  <input
                    type="text"
                    name="ownerName"
                    value={formData.ownerName}
                    onChange={handleChange}
                    placeholder="أدخل اسم المالك"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>رقم الهاتف <span className="required">*</span></label>
                <div className="input-with-icon">
                  <Phone size={18} />
                  <input
                    type="tel"
                    name="ownerPhone"
                    value={formData.ownerPhone}
                    onChange={handleChange}
                    placeholder="05xxxxxxxx"
                    dir="ltr"
                    required
                  />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>عنوان العرض</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="(اختياري) سيتم إنشاء عنوان تلقائياً إذا تركته فارغ"
              />
            </div>
          </div>

          {/* Offer Type */}
          <div className="form-section">
            <h3><DollarSign size={20} /> نوع العرض</h3>
            <div className="property-type-selector">
              <label className={`type-option ${formData.offerType === 'sale' ? 'selected' : ''}`}>
                <input type="radio" name="offerType" value="sale" checked={formData.offerType === 'sale'} onChange={handleChange} />
                <DollarSign size={24} />
                <span>بيع</span>
              </label>
              <label className={`type-option ${formData.offerType === 'rent' ? 'selected' : ''}`}>
                <input type="radio" name="offerType" value="rent" checked={formData.offerType === 'rent'} onChange={handleChange} />
                <Home size={24} />
                <span>إيجار</span>
              </label>
            </div>
          </div>

          {/* Property Type */}
          <div className="form-section">
            <h3><Home size={20} /> نوع العقار</h3>
            <div className="property-type-selector">
              {propertyTypes.map(type => {
                const Icon = type.icon;
                return (
                  <label
                    key={type.value}
                    className={`type-option ${formData.propertyType === type.value ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="propertyType"
                      value={type.value}
                      checked={formData.propertyType === type.value}
                      onChange={handleChange}
                    />
                    <Icon size={24} />
                    <span>{t(type.labelKey)}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Location */}
          <div className="form-section">
            <h3><MapPin size={20} /> الموقع</h3>
            <div className="form-row three-cols">
              <div className="form-group">
                <label>المدينة <span className="required">*</span></label>
                <select name="city" value={formData.city} onChange={handleChange} required disabled={loadingLocations}>
                  <option value="">{loadingLocations ? 'جاري التحميل...' : 'اختر المدينة'}</option>
                  {cities.map(city => (
                    <option key={city.label} value={city.label}>{city.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>المنطقة</label>
                <select name="region" value={formData.region} onChange={handleChange} disabled={!formData.city || loadingLocations}>
                  <option value="">اختر المنطقة</option>
                  {formData.city && (areasByCity[formData.city] || []).map(area => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>الحي <span className="required">*</span></label>
                <select
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  disabled={!formData.city || loadingLocations}
                  required
                >
                  <option value="">اختر الحي</option>
                  {(formData.region
                    ? (districtsByArea[formData.region] || [])
                    : (allDistrictsByCity[formData.city] || [])
                  ).map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Price & Area */}
          <div className="form-section">
            <h3><DollarSign size={20} /> السعر والمساحة</h3>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  name="priceOnRequest"
                  checked={formData.priceOnRequest}
                  onChange={handleChange}
                />
                <span>السعر عند الطلب (لاحقاً)</span>
              </label>
            </div>
            <div className="form-row two-cols">
              <div className="form-group">
                <label>السعر (ر.س) {!formData.priceOnRequest && <span className="required">*</span>}</label>
                <div className="input-with-icon">
                  <DollarSign size={18} />
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="1,000,000"
                    disabled={formData.priceOnRequest}
                    required={!formData.priceOnRequest}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>المساحة (م²) {isSale && <span className="required">*</span>}</label>
                <div className="input-with-icon">
                  <Maximize size={18} />
                  <input
                    type="number"
                    name="area"
                    value={formData.area}
                    onChange={handleChange}
                    placeholder="500"
                    required={isSale}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Specifications */}
          {showSpecs && (
            <div className="form-section">
              <h3><Home size={20} /> المواصفات</h3>
              <div className="form-row three-cols">
                <div className="form-group">
                  <label>عدد الغرف</label>
                  <div className="input-with-icon">
                    <Bed size={18} />
                    <input type="number" name="rooms" value={formData.rooms} onChange={handleChange} placeholder="5" min="0" />
                  </div>
                </div>
                <div className="form-group">
                  <label>دورات المياه</label>
                  <div className="input-with-icon">
                    <Bath size={18} />
                    <input type="number" name="bathrooms" value={formData.bathrooms} onChange={handleChange} placeholder="3" min="0" />
                  </div>
                </div>
                <div className="form-group">
                  <label>عمر العقار (سنة)</label>
                  <div className="input-with-icon">
                    <Calendar size={18} />
                    <input type="number" name="age" value={formData.age} onChange={handleChange} placeholder="5" min="0" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Direction, Street, Floors */}
          <div className="form-section">
            <h3><Compass size={20} /> الواجهة والشارع والأدوار</h3>
            <div className="form-row three-cols">
              <div className="form-group">
                <label>الواجهة</label>
                <select name="direction" value={formData.direction} onChange={handleChange}>
                  <option value="">اختر الواجهة</option>
                  {DIRECTIONS.map(d => (
                    <option key={d} value={d}>{DIRECTION_LABELS[d]}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>عرض الشارع (م)</label>
                <input type="number" name="streetWidth" value={formData.streetWidth} onChange={handleChange} placeholder="15" min="0" />
              </div>
              <div className="form-group">
                <label>عدد الأدوار</label>
                <div className="input-with-icon">
                  <Layers size={18} />
                  <input type="number" name="floors" value={formData.floors} onChange={handleChange} placeholder="2" min="0" />
                </div>
              </div>
            </div>
          </div>

          {/* Deed & Payments */}
          <div className="form-section">
            <h3><FileText size={20} /> الصكوك والدفعات</h3>
            <div className="form-row two-cols">
              <div className="form-group">
                <label>نوع الصك</label>
                <select name="deedType" value={formData.deedType} onChange={handleChange}>
                  <option value="">اختر نوع الصك</option>
                  <option value="free">حر</option>
                  <option value="mortgaged">مرهون</option>
                </select>
              </div>
              {isSale && (
                <div className="form-group">
                  <label>يقبل الدفعات</label>
                  <select name="acceptsPayments" value={formData.acceptsPayments} onChange={handleChange}>
                    <option value="no">لا</option>
                    <option value="yes">نعم</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Utilities (rent only) */}
          {isRent && (
            <div className="form-section">
              <h3><Zap size={20} /> الكهرباء والمياه</h3>
              <div className="form-row two-cols">
                <div className="form-group">
                  <label>الكهرباء</label>
                  <div className="input-with-icon">
                    <Zap size={18} />
                    <select name="electricity" value={formData.electricity} onChange={handleChange}>
                      <option value="independent">مستقل</option>
                      <option value="shared">مشترك</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>المياه</label>
                  <div className="input-with-icon">
                    <Droplet size={18} />
                    <select name="water" value={formData.water} onChange={handleChange}>
                      <option value="independent">مستقل</option>
                      <option value="shared">مشترك</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Offer Nature */}
          <div className="form-section">
            <h3><User size={20} /> صفة العرض</h3>
            <div className="offer-nature-selector">
              <label className={`nature-option ${formData.offerNature === 'direct' ? 'selected' : ''}`}>
                <input type="radio" name="offerNature" value="direct" checked={formData.offerNature === 'direct'} onChange={handleChange} />
                <span>مباشر</span>
              </label>
              <label className={`nature-option ${formData.offerNature === 'withDirect' ? 'selected' : ''}`}>
                <input type="radio" name="offerNature" value="withDirect" checked={formData.offerNature === 'withDirect'} onChange={handleChange} />
                <span>مع المباشر</span>
              </label>
              <label className={`nature-option ${formData.offerNature === 'multiple' ? 'selected' : ''}`}>
                <input type="radio" name="offerNature" value="multiple" checked={formData.offerNature === 'multiple'} onChange={handleChange} />
                <span>معي 2 او اكثر</span>
              </label>
            </div>
          </div>

          {/* Features */}
          <div className="form-section">
            <h3><Sparkles size={20} /> الخصائص الإضافية</h3>
            <div className="form-row two-cols">
              <div className="form-group">
                <input
                  type="text"
                  value={featureInput}
                  onChange={(e) => setFeatureInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddFeature();
                    }
                  }}
                  placeholder="مثال: مسبح، مصعد، حديقة..."
                />
              </div>
              <div className="form-group">
                <button type="button" className="btn btn-secondary" onClick={handleAddFeature}>
                  إضافة
                </button>
              </div>
            </div>
            {formData.features.length > 0 && (
              <div className="features-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                {formData.features.map(f => (
                  <span
                    key={f}
                    className="feature-chip"
                    style={{
                      background: 'var(--background-color)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 16,
                      padding: '6px 12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    {f}
                    <button
                      type="button"
                      onClick={() => handleRemoveFeature(f)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex' }}
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Description */}
          <div className="form-section">
            <h3><Building2 size={20} /> وصف العقار</h3>
            <div className="form-group">
              <label>وصف تفصيلي</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={6}
                placeholder="اكتب وصفاً تفصيلياً للعقار، مميزاته، موقعه..."
              />
            </div>
            <div className="form-group">
              <label>ملاحظات خاصة (تظهر لك فقط)</label>
              <textarea
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                rows={3}
                placeholder="(اختياري) معلومات إضافية أو موقع تفصيلي"
              />
            </div>
          </div>

          {/* Images */}
          <div className="form-section">
            <h3><ImageIcon size={20} /> الوسائط</h3>
            <p className="form-hint">أضف الصور والفيديوهات فقط. الصورة الأولى ستكون الصورة الرئيسية للعرض.</p>
            <div className="form-group">
              <label>إضافة صور (حتى 10)</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImagesUpload}
                disabled={imageFiles.length >= 10}
              />
              {imagePreviews.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                  {imagePreviews.map((src, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img src={src} alt={`img-${idx}`} style={{ width: 100, height: 80, objectFit: 'cover', borderRadius: 8 }} />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(idx)}
                        style={{
                          position: 'absolute',
                          top: 2,
                          left: 2,
                          background: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Videos share the same media section and validation stays unchanged. */}
            <div className="form-group">
              <label><VideoIcon size={18} /> إضافة فيديوهات (حتى 5)</label>
              <input
                type="file"
                accept="video/*"
                multiple
                onChange={handleVideosUpload}
                disabled={videoFiles.length >= 5}
              />
              {videoPreviews.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                  {videoPreviews.map((src, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <video src={src} style={{ width: 160, height: 100, objectFit: 'cover', borderRadius: 8 }} muted />
                      <button
                        type="button"
                        onClick={() => handleRemoveVideo(idx)}
                        style={{
                          position: 'absolute',
                          top: 2,
                          left: 2,
                          background: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          </>
          )}

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? (
              <>
                <Loader2 size={20} className="spinning" />
                جاري الإرسال...
              </>
            ) : (
              <>
                <Send size={20} />
                إرسال العرض
              </>
            )}
          </button>
        </form>

        <div className="form-footer">
          <p>بتقديم هذا النموذج، أنت توافق على مشاركة بيانات العقار مع المسوق العقاري</p>
        </div>
      </div>
    </div>
  );
};

export default PublicOfferForm;
